
import {
  defineComponent,
  ref,
  onMounted,
  nextTick,
  computed,
  watch,
  onBeforeUnmount,
} from "vue";
import { useI18n } from "vue-i18n";
import * as echarts from "echarts";
import Api from "@/api/userService";
import DeviceInfoResponse, { DeviceInfo } from "@/model/DeviceInfoResponse";
import CleanInfoResponse, { CleanInfo } from "@/model/CleanInfoResponse";
import EnergyUsageResponse, { EnergyUsage } from "@/model/EnergyUsageResponse";
import TaskCountResponse, { TaskCountInfo } from "@/model/TaskCountResponse";
import CleanAreaStatisticResponse, {
  CleanAreaStatistic,
} from "@/model/CleanAreaStatisticResponse";
import CleanTimeStatisticResponse, {
  CleanTimeStatistic,
} from "@/model/CleanTimeStatisticResponse";
import CleanRateStatisticResponse, {
  CleanRateStatistic,
} from "@/model/CleanRateStatisticResponse";
import TaskEndCountResponse, {
  TaskEndCountResult,
} from "@/model/TaskEndCountResponse";

export default defineComponent({
  name: "DashboardView",
  setup() {
    const { t } = useI18n();

    // 任务结束原因映射
    const taskEndReasonKeyMap: Record<string, string> = {
      "1": "taskEndNormal",
      "3": "taskEndAbnormal",
      "4": "taskEndPreparationFail",
      "10": "taskEndSupply",
      "11": "taskEndShutdown",
      "12": "taskEndLocalizationFail",
      "14": "taskEndRescueTimeout",
      "18": "taskEndGlobalPathFail",
      "19": "taskEndDispatchPathFail",
      "20": "taskEndNearGoalRescueFail",
      "27": "taskEndLowWaterSkip",
      "29": "taskEndCrossFloorFail",
      "34": "taskEndNodeFault",
      "38": "taskEndPedalPressed",
      "44": "taskEndSewageAbnormal",
      "-405": "taskEndTargetUnreachable",
      "-406": "taskEndStartInVirtualWall",
      "9999": "taskEndUnknown",
    };

    const taskEndReasonCodes = Object.keys(taskEndReasonKeyMap);

    // 清洁信息数据
    const cleanInfo = ref<CleanInfo>({
      sc50: {
        total_clean_area: 0,
        total_clean_time: 0,
      },
      sc80: {
        total_clean_area: 0,
        total_clean_time: 0,
      },
      mix1: {
        total_clean_area: 0,
        total_clean_time: 0,
      },
    });

    // 能耗统计数据
    const energyUsage = ref<EnergyUsage>({
      sc50: {
        total_use_water: 0,
        total_power_consumption: 0,
      },
      sc80: {
        total_use_water: 0,
        total_power_consumption: 0,
      },
      mix1: {
        total_use_water: 0,
        total_power_consumption: 0,
      },
    });

    // 创建空任务结束原因统计数据
    const createEmptyTaskEndSeries = () => ({
      "1": { task_count: 0 },
      "3": { task_count: 0 },
      "4": { task_count: 0 },
      "10": { task_count: 0 },
      "11": { task_count: 0 },
      "12": { task_count: 0 },
      "14": { task_count: 0 },
      "18": { task_count: 0 },
      "19": { task_count: 0 },
      "20": { task_count: 0 },
      "27": { task_count: 0 },
      "29": { task_count: 0 },
      "34": { task_count: 0 },
      "38": { task_count: 0 },
      "44": { task_count: 0 },
      "-405": { task_count: 0 },
      "-406": { task_count: 0 },
      "9999": { task_count: 0 },
    });

    // 任务结束原因统计数据
    const taskEndCount = ref<TaskEndCountResult>({
      sc50: createEmptyTaskEndSeries(),
      sc80: createEmptyTaskEndSeries(),
      mix1: createEmptyTaskEndSeries(),
    });

    // 任务总结数据
    const taskCount = ref<TaskCountInfo>({
      success_sc50: { task_count: 0 },
      success_sc80: { task_count: 0 },
      success_mix1: { task_count: 0 },
      abnormal_sc50: { task_count: 0 },
      abnormal_sc80: { task_count: 0 },
      abnormal_mix1: { task_count: 0 },
    });

    // 设备信息数据
    const deviceInfo = ref({
      sc50_count: "0",
      sc80_count: "0",
      mix1_count: "0",
      sc50_online_count: "0",
      sc80_online_count: "0",
      mix1_online_count: "0",
    });

    const totalDeviceChart = ref<HTMLDivElement | null>(null);
    const onlineDeviceChart = ref<HTMLDivElement | null>(null);
    const totalCleanAreaChart = ref<HTMLDivElement | null>(null);
    const totalCleanTimeChart = ref<HTMLDivElement | null>(null);
    const taskSummaryChart = ref<HTMLDivElement | null>(null);
    const taskReasonOverviewChart = ref<HTMLDivElement | null>(null);
    const timeChart = ref<HTMLDivElement | null>(null);
    const areaChart = ref<HTMLDivElement | null>(null);
    const efficiencyChart = ref<HTMLDivElement | null>(null);
    const energyUsageBarChart = ref<HTMLDivElement | null>(null);

    // 数字解析
    const parseToNumber = (value?: string | number) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };

    // 数字格式化
    const formatNumber = (
      value: string | number,
      fractionDigits = 0
    ): string => {
      const num = parseToNumber(value);
      return num.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    };

    // 设备数量统计数据
    const totalDeviceCount = computed(() => {
      const sc50 = parseToNumber(deviceInfo.value.sc50_count);
      const sc80 = parseToNumber(deviceInfo.value.sc80_count);
      const mix1 = parseToNumber(deviceInfo.value.mix1_count);
      return (sc50 + sc80 + mix1).toString();
    });

    // 在线设备数量统计数据
    const totalOnlineCount = computed(() => {
      const sc50 = parseToNumber(deviceInfo.value.sc50_online_count);
      const sc80 = parseToNumber(deviceInfo.value.sc80_online_count);
      const mix1 = parseToNumber(deviceInfo.value.mix1_online_count);
      return (sc50 + sc80 + mix1).toString();
    });

    // 清洁面积统计数据
    const totalCleanArea = computed(() => {
      const sc50 = parseToNumber(cleanInfo.value.sc50.total_clean_area);
      const sc80 = parseToNumber(cleanInfo.value.sc80.total_clean_area);
      const mix1 = parseToNumber(cleanInfo.value.mix1.total_clean_area);
      return sc50 + sc80 + mix1;
    });

    // 清洁时间统计数据
    const totalCleanTime = computed(() => {
      const sc50 = parseToNumber(cleanInfo.value.sc50.total_clean_time);
      const sc80 = parseToNumber(cleanInfo.value.sc80.total_clean_time);
      const mix1 = parseToNumber(cleanInfo.value.mix1.total_clean_time);
      return sc50 + sc80 + mix1;
    });

    // 设备分布数据
    const deviceDistribution = computed(() => {
      const sc50 = parseToNumber(deviceInfo.value.sc50_count);
      const sc80 = parseToNumber(deviceInfo.value.sc80_count);
      const mix1 = parseToNumber(deviceInfo.value.mix1_count);
      // 如果数据都为0，给一个最小值确保标签能显示
      const total = sc50 + sc80 + mix1;
      if (total === 0) {
        return [
          { name: "SC50", value: 0 },
          { name: "SC80", value: 0 },
          { name: "MIX1", value: 0 },
        ];
      }
      return [
        { name: "SC50", value: sc50 },
        { name: "SC80", value: sc80 },
        { name: "MIX1", value: mix1 },
      ];
    });

    // 在线设备分布数据
    const onlineDistribution = computed(() => [
      {
        name: "SC50",
        value: parseToNumber(deviceInfo.value.sc50_online_count),
      },
      {
        name: "SC80",
        value: parseToNumber(deviceInfo.value.sc80_online_count),
      },
      {
        name: "MIX1",
        value: parseToNumber(deviceInfo.value.mix1_online_count),
      },
    ]);

    // 清洁面积分布数据
    const cleanAreaDistribution = computed(() => [
      {
        name: "SC50",
        value: parseToNumber(cleanInfo.value.sc50.total_clean_area),
      },
      {
        name: "SC80",
        value: parseToNumber(cleanInfo.value.sc80.total_clean_area),
      },
      {
        name: "MIX1",
        value: parseToNumber(cleanInfo.value.mix1.total_clean_area),
      },
    ]);

    // 清洁时间分布数据
    const cleanTimeDistribution = computed(() => [
      {
        name: "SC50",
        value: parseToNumber(cleanInfo.value.sc50.total_clean_time),
      },
      {
        name: "SC80",
        value: parseToNumber(cleanInfo.value.sc80.total_clean_time),
      },
      {
        name: "MIX1",
        value: parseToNumber(cleanInfo.value.mix1.total_clean_time),
      },
    ]);

    // 用水量统计数据
    const totalWaterUsage = computed(() => {
      const sc50 = parseToNumber(energyUsage.value.sc50.total_use_water);
      const sc80 = parseToNumber(energyUsage.value.sc80.total_use_water);
      const mix1 = parseToNumber(energyUsage.value.mix1.total_use_water);
      return sc50 + sc80 + mix1;
    });

    // 能耗统计数据
    const totalPowerConsumption = computed(() => {
      const sc50 = parseToNumber(
        energyUsage.value.sc50.total_power_consumption
      );
      const sc80 = parseToNumber(
        energyUsage.value.sc80.total_power_consumption
      );
      const mix1 = parseToNumber(
        energyUsage.value.mix1.total_power_consumption
      );
      return sc50 + sc80 + mix1;
    });

    // 任务总结数据
    const taskSummaryData = computed(() => {
      const success50 = parseToNumber(taskCount.value.success_sc50.task_count);
      const success80 = parseToNumber(taskCount.value.success_sc80.task_count);
      const successMix1 = parseToNumber(
        taskCount.value.success_mix1.task_count
      );
      const abnormal50 = parseToNumber(
        taskCount.value.abnormal_sc50.task_count
      );
      const abnormal80 = parseToNumber(
        taskCount.value.abnormal_sc80.task_count
      );
      const abnormalMix1 = parseToNumber(
        taskCount.value.abnormal_mix1.task_count
      );

      return {
        all: {
          success: success50 + success80 + successMix1,
          abnormal: abnormal50 + abnormal80 + abnormalMix1,
        },
        sc50: {
          success: success50,
          abnormal: abnormal50,
        },
        sc80: {
          success: success80,
          abnormal: abnormal80,
        },
        mix1: {
          success: successMix1,
          abnormal: abnormalMix1,
        },
      };
    });

    const taskSummaryDisplay = computed(
      () => taskSummaryData.value[taskStatsFilter.value]
    );

    // 任务成功分布数据
    const successTaskDistribution = computed(() => [
      {
        name: "SC50",
        value: parseToNumber(taskCount.value.success_sc50.task_count),
      },
      {
        name: "SC80",
        value: parseToNumber(taskCount.value.success_sc80.task_count),
      },
      {
        name: "MIX1",
        value: parseToNumber(taskCount.value.success_mix1.task_count),
      },
    ]);

    // 任务异常分布数据
    const abnormalTaskDistribution = computed(() => [
      {
        name: "SC50",
        value: parseToNumber(taskCount.value.abnormal_sc50.task_count),
      },
      {
        name: "SC80",
        value: parseToNumber(taskCount.value.abnormal_sc80.task_count),
      },
      {
        name: "MIX1",
        value: parseToNumber(taskCount.value.abnormal_mix1.task_count),
      },
    ]);

    // 任务结束原因概览数据
    const taskReasonOverview = computed(() => {
      const buildDataset = (mode: "all" | "sc50" | "sc80" | "mix1") => {
        return taskEndReasonCodes.map((code) => {
          const sc50 = parseToNumber(
            taskEndCount.value.sc50?.[code]?.task_count
          );
          const sc80 = parseToNumber(
            taskEndCount.value.sc80?.[code]?.task_count
          );
          const mix1 = parseToNumber(
            taskEndCount.value.mix1?.[code]?.task_count
          );
          let value = mix1;
          if (mode === "all") {
            value = sc50 + sc80 + mix1;
          } else if (mode === "sc50") {
            value = sc50;
          } else if (mode === "sc80") {
            value = sc80;
          }
          return {
            code,
            name: t(taskEndReasonKeyMap[code]),
            value,
          };
        });
      };

      return {
        all: buildDataset("all"),
        sc50: buildDataset("sc50"),
        sc80: buildDataset("sc80"),
        mix1: buildDataset("mix1"),
      };
    });

    // 任务结束原因饼图数据
    const taskEndReasonSeries = computed(() => {
      return taskEndReasonCodes.map((code) => {
        const sc50Value = parseToNumber(
          taskEndCount.value.sc50?.[code]?.task_count
        );
        const sc80Value = parseToNumber(
          taskEndCount.value.sc80?.[code]?.task_count
        );
        const mix1Value = parseToNumber(
          taskEndCount.value.mix1?.[code]?.task_count
        );
        return {
          code,
          name: t(taskEndReasonKeyMap[code]),
          total: sc50Value + sc80Value + mix1Value,
          sc50: sc50Value,
          sc80: sc80Value,
          mix1: mix1Value,
        };
      });
    });

    // 任务结束原因饼图数据
    const taskEndPieData = computed(() => {
      const series = taskEndReasonSeries.value.map((item) => ({
        name: item.name,
        value: item.total,
        realValue: item.total,
        sc50: item.sc50,
        sc80: item.sc80,
        mix1: item.mix1,
        code: item.code,
      }));

      const totalReal = series.reduce(
        (sum, item) => sum + (item.realValue ?? 0),
        0
      );

      if (totalReal === 0) {
        return series.map((item) => ({
          ...item,
          value: 1,
          realValue: 0,
          sc50: 0,
          sc80: 0,
          mix1: 0,
          isZeroPlaceholder: true,
        }));
      }

      return series.filter((item) => item.realValue ?? 0);
    });

    // 新增的时间范围控制
    const areaTimeRange = ref<number>(7);
    const timeTimeRange = ref<number>(7);
    const efficiencyTimeRange = ref<number>(7);
    const efficiencyFilter = ref<"all" | "sc50" | "sc80" | "mix1">("all");
    const taskStatsFilter = ref<"all" | "sc50" | "sc80" | "mix1">("all");

    // 清扫面积统计数据
    const cleanAreaData = ref<{
      cleanArea_sc50: number[];
      planCleanArea_sc50: number[];
      dates_sc50: string[];
      cleanArea_sc80: number[];
      planCleanArea_sc80: number[];
      dates_sc80: string[];
      cleanArea_mix1: number[];
      planCleanArea_mix1: number[];
      dates_mix1: string[];
    }>({
      cleanArea_sc50: [],
      planCleanArea_sc50: [],
      dates_sc50: [],
      cleanArea_sc80: [],
      planCleanArea_sc80: [],
      dates_sc80: [],
      cleanArea_mix1: [],
      planCleanArea_mix1: [],
      dates_mix1: [],
    });

    // 清洁时间统计数据
    const cleanTimeData = ref<{
      cleanTime: number[];
      dates: string[];
      cleanTime_sc50?: number[];
      dates_sc50?: string[];
      cleanTime_sc80?: number[];
      dates_sc80?: string[];
      cleanTime_mix1?: number[];
      dates_mix1?: string[];
    }>({
      cleanTime: [],
      dates: [],
      cleanTime_sc50: [],
      dates_sc50: [],
      cleanTime_sc80: [],
      dates_sc80: [],
      cleanTime_mix1: [],
      dates_mix1: [],
    });

    // 清扫效率统计数据
    const cleanRateData = ref<{
      cleanRate_sc50: number[];
      maxCleanRate_sc50: number[];
      dates_sc50: string[];
      cleanRate_sc80: number[];
      maxCleanRate_sc80: number[];
      dates_sc80: string[];
      cleanRate_mix1: number[];
      maxCleanRate_mix1: number[];
      dates_mix1: string[];
      cleanRate_all: number[];
      maxCleanRate_all: number[];
      dates_all: string[];
    }>({
      cleanRate_sc50: [],
      maxCleanRate_sc50: [],
      dates_sc50: [],
      cleanRate_sc80: [],
      maxCleanRate_sc80: [],
      dates_sc80: [],
      cleanRate_mix1: [],
      maxCleanRate_mix1: [],
      dates_mix1: [],
      cleanRate_all: [],
      maxCleanRate_all: [],
      dates_all: [],
    });

    // 新增的时间范围切换方法
    const changeAreaTimeRange = (days: number) => {
      areaTimeRange.value = days;
      loadCleanAreaStatistic(days);
    };

    const changeTimeTimeRange = (days: number) => {
      timeTimeRange.value = days;
      loadCleanTimeStatistic(days);
    };

    let totalDeviceChartInstance: echarts.ECharts | null = null;
    let onlineDeviceChartInstance: echarts.ECharts | null = null;
    let totalCleanAreaChartInstance: echarts.ECharts | null = null;
    let totalCleanTimeChartInstance: echarts.ECharts | null = null;
    let taskSummaryChartInstance: echarts.ECharts | null = null;
    let taskReasonOverviewChartInstance: echarts.ECharts | null = null;
    let timeChartInstance: echarts.ECharts | null = null;
    let areaChartInstance: echarts.ECharts | null = null;
    let efficiencyChartInstance: echarts.ECharts | null = null;
    let energyUsageBarChartInstance: echarts.ECharts | null = null;

    // 设备饼图配置 .
    const buildDevicePieOption = (
      data: { name: string; value: number }[],
      colors: string[] = ["#409EFF", "#5BC0DE", "#E6A23C"],
      isOnline = false // 标识是在线设备还是设备总数
    ) => {
      // 根据窗口宽度动态计算字体大小、labelLine 长度和饼图大小
      const windowWidth = window.innerWidth;
      let labelFontSize = 10;
      let boldFontSize = 12;
      let labelLineLength = 12;
      let labelLineLength2 = 6;
      let minMargin = 6;
      let edgeDistance = 8;
      let pieRadiusInner = "26%";
      let pieRadiusOuter = "40%";

      // 根据窗口宽度调整
      if (windowWidth <= 1200) {
        labelFontSize = 9;
        boldFontSize = 10;
        labelLineLength = 8;
        labelLineLength2 = 4;
        minMargin = 4;
        edgeDistance = 6;
        pieRadiusInner = "20%"; // 窗口缩小时减小饼图，为 label 留出更多空间
        pieRadiusOuter = "32%";
      } else if (windowWidth <= 1400) {
        labelFontSize = 10;
        boldFontSize = 12;
        labelLineLength = 10;
        labelLineLength2 = 5;
        minMargin = 5;
        edgeDistance = 7;
        pieRadiusInner = "23%";
        pieRadiusOuter = "36%";
      } else if (windowWidth <= 1600) {
        labelFontSize = 10.5;
        boldFontSize = 12.5;
        labelLineLength = 11;
        labelLineLength2 = 5.5;
        minMargin = 5.5;
        edgeDistance = 7.5;
        pieRadiusInner = "24%";
        pieRadiusOuter = "38%";
      }

      //
      return {
        grid: {
          left: 0, // 设置为 0，让 containLabel 自动扩展
          right: 0, // 设置为 0，让 containLabel 自动扩展
          top: 0,
          bottom: 0,
          containLabel: true, // 自动调整 grid 大小以包含所有 label，确保完整显示
        },
        tooltip: {
          trigger: "item",
          renderMode: "html", // 使用 HTML 渲染模式，支持样式和换行
          textStyle: {
            fontSize: 11, // 设置字体大小
          },
          confine: true, // 允许 tooltip 超出图表区域显示
          appendToBody: true, // 将 tooltip 追加到 body，确保完全展示
          formatter: (params: any) => {
            const { name, value, percent } = params;
            // 使用 HTML 格式，支持换行
            return `<div style="white-space: normal; word-wrap: break-word; max-width: 300px; font-size: 11px;">
              ${name}: ${value} (${percent}%)
            </div>`;
          },
        },
        series: [
          {
            name: "",
            type: "pie",
            radius: [pieRadiusInner, pieRadiusOuter], // 根据窗口大小动态调整，为 label 留出更多空间
            center: ["50%", "50%"], // 在 grid 中居中，让左右两侧 label 都有足够空间
            startAngle: 25, // 从3点方向（右侧）开始，这样两个扇形会分布在上下位置
            data,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: "rgba(0, 0, 0, 0.5)",
              },
            },
            label: {
              show: true,
              position: "outside", // 标签显示在外部
              formatter: (params: any) => {
                // 显示真实值，如果value是占位值1，则显示0
                let realValue = 0;
                if (isOnline) {
                  // 在线设备数据
                  const total =
                    parseToNumber(deviceInfo.value.sc50_online_count) +
                    parseToNumber(deviceInfo.value.sc80_online_count) +
                    parseToNumber(deviceInfo.value.mix1_online_count);
                  if (params.value === 1 && total === 0) {
                    realValue = 0;
                  } else {
                    if (params.name === "SC50") {
                      realValue = parseToNumber(
                        deviceInfo.value.sc50_online_count
                      );
                    } else if (params.name === "SC80") {
                      realValue = parseToNumber(
                        deviceInfo.value.sc80_online_count
                      );
                    } else {
                      realValue = parseToNumber(
                        deviceInfo.value.mix1_online_count
                      );
                    }
                  }
                } else {
                  // 设备总数数据
                  const total =
                    parseToNumber(deviceInfo.value.sc50_count) +
                    parseToNumber(deviceInfo.value.sc80_count) +
                    parseToNumber(deviceInfo.value.mix1_count);
                  if (params.value === 1 && total === 0) {
                    realValue = 0;
                  } else {
                    if (params.name === "SC50") {
                      realValue = parseToNumber(deviceInfo.value.sc50_count);
                    } else if (params.name === "SC80") {
                      realValue = parseToNumber(deviceInfo.value.sc80_count);
                    } else {
                      realValue = parseToNumber(deviceInfo.value.mix1_count);
                    }
                  }
                }
                return `${params.name}: {bold|${realValue}}`;
              },
              fontSize: labelFontSize, // 根据窗口大小动态调整
              color: "#606266",
              fontWeight: "normal",
              minAngle: 0, // 允许显示0度角的标签
              minMargin: minMargin, // 根据窗口大小动态调整
              edgeDistance: edgeDistance, // 根据窗口大小动态调整
              lineHeight: 14,
              overflow: "none", // 不截断文本，确保完整显示
              width: null, // 不限制宽度，让文本自然显示
              rich: {
                bold: {
                  fontSize: boldFontSize, // 根据窗口大小动态调整
                  fontWeight: "bold",
                  color: "#606266",
                },
              },
            },
            avoidLabelOverlap: true, // 避免标签重叠
            labelLine: {
              show: true, // 显示引导线
              length: labelLineLength, // 根据窗口大小动态调整
              length2: labelLineLength2, // 根据窗口大小动态调整
              maxSurfaceAngle: 90, // 增加最大表面角度
              smooth: false,
              lineStyle: {
                width: 1,
                // 不设置 color，让 labelLine 自动使用对应扇形的颜色
              },
            },
          },
        ],
        color: colors,
      };
    };

    // 任务总结饼图
    const buildTaskSummaryOption = (
      summary: { success: number; abnormal: number },
      chartInstance?: echarts.ECharts | null
    ) => {
      const total = summary.success + summary.abnormal;
      const successValue = summary.success === 0 ? 1 : summary.success;
      const abnormalValue = summary.abnormal === 0 ? 1 : summary.abnormal;
      const hasRealData = total > 0;
      return {
        grid: {
          left: "20%",
          right: "30%",
          top: "10%",
          bottom: "10%",
          containLabel: true,
        },
        color: ["#3ad5b2", "#ffad3f"],
        title: [
          {
            text: formatNumber(total, 0),
            left: "center",
            top: "42%",
            textStyle: {
              fontSize: 20,
              fontWeight: "bold",
              color: "#303133",
            },
          },
          {
            text: t("taskTotalCount"),
            left: "center",
            top: "60%",
            textStyle: {
              fontSize: 12,
              color: "#909399",
            },
          },
        ],
        tooltip: {
          trigger: "item",
          formatter: (params: any) =>
            `${params.marker}${params.name}: ${formatNumber(
              params.data?.realValue ?? params.value,
              0
            )}${hasRealData ? ` (${formatNumber(params.percent, 0)}%)` : ""}`,
        },
        series: [
          {
            name: "",
            type: "pie",
            radius: ["55%", "80%"],
            center: ["50%", "50%"],
            avoidLabelOverlap: true,
            label: {
              show: true,
              position: "outside",
              formatter: (params: any) => {
                const realValue = params.data?.realValue ?? params.value ?? 0;
                return formatNumber(realValue, 0);
              },
              fontSize: 14,
              fontWeight: "bold",
              minAngle: 0,
              minMargin: 5,
              edgeDistance: 10,
              lineHeight: 10,
            },
            labelLine: {
              show: true,
              length: 15,
              length2: 10,
              maxSurfaceAngle: 80,
              smooth: false,
              lineStyle: {
                width: 1,
                // 不设置 color，让 labelLine 自动使用对应扇形的颜色
              },
            },

            data: [
              {
                value: successValue,
                name: t("tasksuccesscount"),
                realValue: summary.success,
                labelLine: {
                  lineStyle: {
                    color: "#3ad5b2",
                  },
                },
              },
              {
                value: abnormalValue,
                name: t("taskabnormalcount"),
                realValue: summary.abnormal,
                labelLine: {
                  lineStyle: {
                    color: "#ffad3f",
                  },
                },
              },
            ],
          },
        ],
      };
    };

    // 任务结束原因概览饼图
    const buildTaskReasonOverviewOption = (
      data: { name: string; value: number }[]
    ) => {
      const filtered = data.filter((item) => item.value > 0);
      const seriesData =
        filtered.length > 0
          ? filtered
          : data.map((item) => ({ ...item, value: 0 }));

      return {
        grid: {
          left: "10%",
          right: "10%",
          top: "5%",
          bottom: "10%",
          containLabel: true,
        },

        tooltip: {
          trigger: "item",
          formatter: (params: any) =>
            `${params.marker}${params.name}: ${formatNumber(
              params.value,
              0
            )} (${formatNumber(params.percent, 0)}%)`,
        },
        // legend: {
        //   type: "scroll",
        //   orient: "vertical",
        //   right: 10,
        //   top: 20,
        //   bottom: 20,
        //   data: seriesData,
        // },
        series: [
          {
            name: "",
            type: "pie",
            radius: "50%",
            center: ["50%", "45%"],
            data: seriesData,
            avoidLabelOverlap: true,
            label: {
              show: true,
              position: "outside",
              formatter: (params: any) => {
                const value = params.value ?? 0;
                return `${params.name}: ${formatNumber(value, 0)}`;
              },
              fontSize: 11,
              color: "#303133",
              minAngle: 0,
            },
            labelLine: {
              show: true,
              length: 30,
              length2: 15,
              smooth: false,
              lineStyle: {
                width: 1,
                // 不设置 color，让 labelLine 自动使用对应扇形的颜色
              },
            },
          },
        ],
      };
    };

    // 构建饼图配置
    const buildPieOption = (
      data: {
        name: string;
        value: number;
        detail?: Record<string, number>;
        realValue?: number;
      }[],
      colors: string[] = ["#409EFF", "#67C23A"],
      tooltipFormatter?: (params: any) => string
    ) => {
      // 根据窗口宽度动态计算饼图大小和字体大小
      const windowWidth = window.innerWidth;
      let pieRadiusInner = "45%";
      let pieRadiusOuter = "75%";
      let tooltipFontSize = 11;

      // 根据窗口宽度调整
      if (windowWidth <= 1200) {
        pieRadiusInner = "35%";
        pieRadiusOuter = "65%";
        tooltipFontSize = 10;
      } else if (windowWidth <= 1400) {
        pieRadiusInner = "40%";
        pieRadiusOuter = "70%";
        tooltipFontSize = 10.5;
      } else if (windowWidth <= 1600) {
        pieRadiusInner = "42%";
        pieRadiusOuter = "72%";
        tooltipFontSize = 10.8;
      }

      return {
        tooltip: {
          trigger: "item",
          renderMode: "html", // 使用 HTML 渲染模式，支持样式和换行
          textStyle: {
            fontSize: tooltipFontSize, // 根据窗口大小动态调整
          },
          confine: true, // 允许 tooltip 超出图表区域显示
          appendToBody: true, // 将 tooltip 追加到 body，确保完全展示
          formatter: tooltipFormatter
            ? tooltipFormatter
            : (params: any) => {
                const { name, value, percent, data } = params;
                const actual = data?.realValue ?? value;
                // 使用 HTML 格式，支持换行
                return `<div style="white-space: normal; word-wrap: break-word; max-width: 300px; font-size: ${tooltipFontSize}px;">
                  ${name}: ${actual} (${percent}%)
                </div>`;
              },
        },
        series: [
          {
            name: "",
            type: "pie",
            radius: [pieRadiusInner, pieRadiusOuter], // 根据窗口大小动态调整
            avoidLabelOverlap: true,
            label: {
              show: false,
            },
            labelLine: {
              show: false,
            },
            data,
          },
        ],
        color: colors,
      };
    };

    // 构建能耗柱状图配置
    const buildEnergyBarOption = () => {
      const categories = ["SC50", "SC80", "MIX1"];
      const waterLabel = `${t("waterconsumption")} (L)`;
      const powerLabel = `${t("powerconsumption")} (kWh)`;

      const waterData = [
        parseToNumber(energyUsage.value.sc50.total_use_water),
        parseToNumber(energyUsage.value.sc80.total_use_water),
        parseToNumber(energyUsage.value.mix1.total_use_water),
      ];

      const powerData = [
        parseToNumber(energyUsage.value.sc50.total_power_consumption),
        parseToNumber(energyUsage.value.sc80.total_power_consumption),
        parseToNumber(energyUsage.value.mix1.total_power_consumption),
      ];

      return {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (params: any) => {
            const lines = params
              .map(
                (item: any) =>
                  `${item.marker}${item.seriesName}: ${formatNumber(
                    item.value,
                    2
                  )}`
              )
              .join("<br/>");
            return `${params[0]?.axisValueLabel ?? ""}<br/>${lines}`;
          },
        },
        legend: {
          right: 10,
          data: [waterLabel, powerLabel],
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: categories,
          axisTick: { alignWithLabel: true },
        },
        yAxis: {
          type: "value",
        },
        series: [
          {
            name: waterLabel,
            type: "bar",
            data: waterData,
            itemStyle: { color: "#3ba0ff" },
            barWidth: "22%",
          },
          {
            name: powerLabel,
            type: "bar",
            data: powerData,
            itemStyle: { color: "#ff9f43" },
            barWidth: "22%",
          },
        ],
      };
    };

    // 获取任务饼图配置
    const getTaskPieConfig = (
      data: { name: string; value: number }[],
      palette: string[]
    ) => {
      const total = data.reduce((sum, item) => sum + item.value, 0);
      const hasRealData = total > 0;

      const enriched = data.map((item) => {
        const realValue = item.value;
        const displayValue = hasRealData ? realValue : 1;
        const percent =
          hasRealData && total > 0 ? (realValue / total) * 100 : 0;

        return {
          ...item,
          value: displayValue,
          realValue,
          displayPercent: percent,
          isZeroPlaceholder: !hasRealData,
        };
      });

      const tooltipFormatter = (params: any) => {
        const actual = params.data?.realValue ?? 0;
        const percent = params.data?.displayPercent ?? 0;
        return `${params.marker}${params.name}: ${formatNumber(
          actual,
          0
        )} (${formatNumber(percent, 0)}%)`;
      };

      return {
        data: enriched,
        colors: palette,
        tooltipFormatter,
      };
    };
    //
    // 渲染设备图表
    const renderDeviceCharts = () => {
      nextTick(() => {
        if (totalDeviceChart.value) {
          totalDeviceChartInstance =
            echarts.getInstanceByDom(totalDeviceChart.value) ??
            echarts.init(totalDeviceChart.value);
          totalDeviceChartInstance.setOption(
            buildDevicePieOption(
              deviceDistribution.value,
              ["#4b8bfe", "#3ad5b2", "#ffad3f"],
              false
            ),
            true
          );
        }

        if (onlineDeviceChart.value) {
          onlineDeviceChartInstance =
            echarts.getInstanceByDom(onlineDeviceChart.value) ??
            echarts.init(onlineDeviceChart.value);
          onlineDeviceChartInstance.setOption(
            buildDevicePieOption(
              onlineDistribution.value,
              ["#4b8bfe", "#3ad5b2", "#ffad3f"],
              true
            ),
            true
          );
        }
      });
    };

    // 渲染清洁图表
    const renderCleanCharts = () => {
      nextTick(() => {
        if (totalCleanAreaChart.value) {
          totalCleanAreaChartInstance =
            echarts.getInstanceByDom(totalCleanAreaChart.value) ??
            echarts.init(totalCleanAreaChart.value);
          totalCleanAreaChartInstance.setOption(
            buildPieOption(cleanAreaDistribution.value, [
              "#409EFF",
              "#67C23A",
              "#E6A23C",
            ]),
            true
          );
        }

        if (totalCleanTimeChart.value) {
          totalCleanTimeChartInstance =
            echarts.getInstanceByDom(totalCleanTimeChart.value) ??
            echarts.init(totalCleanTimeChart.value);
          totalCleanTimeChartInstance.setOption(
            buildPieOption(cleanTimeDistribution.value, [
              "#E6A23C",
              "#F56C6C",
              "#6f63f6",
            ]),
            true
          );
        }
      });
    };

    // 渲染任务总结饼图
    const renderTaskDataCard = () => {
      nextTick(() => {
        const summary = taskSummaryDisplay.value;
        if (taskSummaryChart.value) {
          taskSummaryChartInstance =
            echarts.getInstanceByDom(taskSummaryChart.value) ??
            echarts.init(taskSummaryChart.value);
          taskSummaryChartInstance.setOption(
            buildTaskSummaryOption(summary, taskSummaryChartInstance),
            true
          );
        }

        const reasonData = taskReasonOverview.value[taskStatsFilter.value];
        if (taskReasonOverviewChart.value) {
          taskReasonOverviewChartInstance =
            echarts.getInstanceByDom(taskReasonOverviewChart.value) ??
            echarts.init(taskReasonOverviewChart.value);
          taskReasonOverviewChartInstance.setOption(
            buildTaskReasonOverviewOption(reasonData),
            true
          );
        }
      });
    };

    // 渲染能耗图表
    const renderEnergyCharts = () => {
      nextTick(() => {
        if (energyUsageBarChart.value) {
          energyUsageBarChartInstance =
            echarts.getInstanceByDom(energyUsageBarChart.value) ??
            echarts.init(energyUsageBarChart.value);
          energyUsageBarChartInstance.setOption(buildEnergyBarOption(), true);
        }
      });
    };

    // 处理窗口大小变化
    const handleResize = () => {
      // 先 resize 所有图表
      totalDeviceChartInstance?.resize();
      onlineDeviceChartInstance?.resize();
      totalCleanAreaChartInstance?.resize();
      totalCleanTimeChartInstance?.resize();
      taskSummaryChartInstance?.resize();
      taskReasonOverviewChartInstance?.resize();
      areaChartInstance?.resize();
      timeChartInstance?.resize();
      efficiencyChartInstance?.resize();
      energyUsageBarChartInstance?.resize();

      // 重新设置设备饼图配置，使 label 能够适应新的窗口大小
      if (totalDeviceChartInstance && totalDeviceChart.value) {
        totalDeviceChartInstance.setOption(
          buildDevicePieOption(
            deviceDistribution.value,
            ["#4b8bfe", "#3ad5b2", "#ffad3f"],
            false
          ),
          true
        );
      }

      if (onlineDeviceChartInstance && onlineDeviceChart.value) {
        onlineDeviceChartInstance.setOption(
          buildDevicePieOption(
            onlineDistribution.value,
            ["#4b8bfe", "#3ad5b2", "#ffad3f"],
            true
          ),
          true
        );
      }

      // 重新设置清洁面积和清洁时长饼图配置，使饼图大小能够适应新的窗口大小
      if (totalCleanAreaChartInstance && totalCleanAreaChart.value) {
        totalCleanAreaChartInstance.setOption(
          buildPieOption(cleanAreaDistribution.value, [
            "#409EFF",
            "#67C23A",
            "#E6A23C",
          ]),
          true
        );
      }

      if (totalCleanTimeChartInstance && totalCleanTimeChart.value) {
        totalCleanTimeChartInstance.setOption(
          buildPieOption(cleanTimeDistribution.value, [
            "#E6A23C",
            "#F56C6C",
            "#6f63f6",
          ]),
          true
        );
      }
    };

    // 切换清扫效率时间范围
    const changeEfficiencyTimeRange = (days: number) => {
      efficiencyTimeRange.value = days;
      loadCleanRateStatistic(days);
    };

    // 切换清扫效率筛选器
    const changeEfficiencyFilter = (
      filter: "all" | "sc50" | "sc80" | "mix1"
    ) => {
      if (efficiencyFilter.value !== filter) {
        efficiencyFilter.value = filter;
        // 只更新图表显示，不重新请求数据（watch 会自动触发 initEfficiencyChart2）
      }
    };

    // 切换任务统计过滤器
    const changeTaskStatsFilter = (
      filter: "all" | "sc50" | "sc80" | "mix1"
    ) => {
      if (taskStatsFilter.value !== filter) {
        taskStatsFilter.value = filter;
      }
    };

    // 获取设备信息
    const loadDeviceInfo = async () => {
      try {
        const deviceResponse: DeviceInfoResponse = await Api.getDeviceInfo();
        if (deviceResponse.err_no === 200) {
          const result = deviceResponse.result as DeviceInfo | undefined;
          deviceInfo.value.sc50_count = String(result?.sc50_count ?? "0");
          deviceInfo.value.sc50_online_count = String(
            result?.sc50_online_count ?? "0"
          );
          deviceInfo.value.sc80_count = String(result?.sc80_count ?? "0");
          deviceInfo.value.sc80_online_count = String(
            result?.sc80_online_count ?? "0"
          );
          deviceInfo.value.mix1_count = String(result?.mix1_count ?? "0");
          deviceInfo.value.mix1_online_count = String(
            result?.mix1_online_count ?? "0"
          );
          renderDeviceCharts();
        }
      } catch (error) {
        console.error("加载设备信息失败:", error);
      }
    };

    // 获取清扫效率统计
    const loadCleanRateStatistic = async (dayNum = 7) => {
      try {
        const rateResponse: CleanRateStatisticResponse =
          await Api.getCleanRateStatistic(dayNum);
        if (rateResponse.err_no === 200) {
          const result =
            rateResponse.result as CleanRateStatisticResponse["result"];

          // 处理 sc50 数据
          cleanRateData.value.cleanRate_sc50 = Array.isArray(
            result.sc50.clean_rate
          )
            ? result.sc50.clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.maxCleanRate_sc50 = Array.isArray(
            result.sc50.max_clean_rate
          )
            ? result.sc50.max_clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.dates_sc50 = result.sc50.date || [];

          // 处理 sc80 数据
          cleanRateData.value.cleanRate_sc80 = Array.isArray(
            result.sc80.clean_rate
          )
            ? result.sc80.clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.maxCleanRate_sc80 = Array.isArray(
            result.sc80.max_clean_rate
          )
            ? result.sc80.max_clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.dates_sc80 = result.sc80.date || [];

          // 处理 mix1 数据
          cleanRateData.value.cleanRate_mix1 = Array.isArray(
            result.mix1.clean_rate
          )
            ? result.mix1.clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.maxCleanRate_mix1 = Array.isArray(
            result.mix1.max_clean_rate
          )
            ? result.mix1.max_clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.dates_mix1 = result.mix1.date || [];

          // 处理 all 数据
          cleanRateData.value.cleanRate_all = Array.isArray(
            result.all.clean_rate
          )
            ? result.all.clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.maxCleanRate_all = Array.isArray(
            result.all.max_clean_rate
          )
            ? result.all.max_clean_rate.map((item) => Number(item))
            : [];
          cleanRateData.value.dates_all = result.all.date || [];

          console.log("清扫效率统计加载成功:", rateResponse.result);
          // 更新图表
          initEfficiencyChart2();
        }
      } catch (error) {
        console.error("加载清扫效率统计失败:", error);
      }
    };

    // 获取清洁时长统计
    const loadCleanTimeStatistic = async (dayNum = 7) => {
      try {
        const timeResponse: CleanTimeStatisticResponse =
          await Api.getCleanTimeStatistic(dayNum);
        if (timeResponse.err_no === 200) {
          const result: CleanTimeStatistic = timeResponse.result;

          // 处理 sc50 数据
          cleanTimeData.value.cleanTime_sc50 = Array.isArray(
            result.sc50.clean_time
          )
            ? result.sc50.clean_time.map((item) => Number(item))
            : [];
          cleanTimeData.value.dates_sc50 = result.sc50.date || [];

          // 处理 sc80 数据
          cleanTimeData.value.cleanTime_sc80 = Array.isArray(
            result.sc80.clean_time
          )
            ? result.sc80.clean_time.map((item) => Number(item))
            : [];
          cleanTimeData.value.dates_sc80 = result.sc80.date || [];

          // 处理 mix1 数据
          cleanTimeData.value.cleanTime_mix1 = Array.isArray(
            result.mix1.clean_time
          )
            ? result.mix1.clean_time.map((item) => Number(item))
            : [];
          cleanTimeData.value.dates_mix1 = result.mix1.date || [];

          console.log("清扫时间统计加载成功:", timeResponse.result);
          // 更新图表
          initTimeChart();
        }
      } catch (error) {
        console.error("加载清扫时间统计失败:", error);
      }
    };

    // 获取清扫面积统计
    const loadCleanAreaStatistic = async (dayNum = 7) => {
      try {
        const areaResponse: CleanAreaStatisticResponse =
          await Api.getCleanAreaStatistic(dayNum);
        if (areaResponse.err_no === 200) {
          const result = areaResponse.result as CleanAreaStatistic;

          // 处理 sc50 数据
          cleanAreaData.value.cleanArea_sc50 = Array.isArray(
            result.sc50.clean_area
          )
            ? result.sc50.clean_area.map((item) => Number(item))
            : [];
          cleanAreaData.value.planCleanArea_sc50 = Array.isArray(
            result.sc50.plan_clean_area
          )
            ? result.sc50.plan_clean_area.map((item) => Number(item))
            : [];
          cleanAreaData.value.dates_sc50 = result.sc50.date || [];

          // 处理 sc80 数据
          cleanAreaData.value.cleanArea_sc80 = Array.isArray(
            result.sc80.clean_area
          )
            ? result.sc80.clean_area.map((item) => Number(item))
            : [];
          cleanAreaData.value.planCleanArea_sc80 = Array.isArray(
            result.sc80.plan_clean_area
          )
            ? result.sc80.plan_clean_area.map((item) => Number(item))
            : [];
          cleanAreaData.value.dates_sc80 = result.sc80.date || [];

          // 处理 mix1 数据
          cleanAreaData.value.cleanArea_mix1 = Array.isArray(
            result.mix1.clean_area
          )
            ? result.mix1.clean_area.map((item) => Number(item))
            : [];
          cleanAreaData.value.planCleanArea_mix1 = Array.isArray(
            result.mix1.plan_clean_area
          )
            ? result.mix1.plan_clean_area.map((item) => Number(item))
            : [];
          cleanAreaData.value.dates_mix1 = result.mix1.date || [];

          console.log("清扫面积统计加载成功:", areaResponse.result);
          // 更新图表
          initAreaChart();
        }
      } catch (error) {
        console.error("加载清扫面积统计失败:", error);
      }
    };

    // 获取清扫信息
    const loadCleanInfo = async () => {
      try {
        const cleanResponse: CleanInfoResponse = await Api.getCleanInfo();
        if (cleanResponse.err_no === 200) {
          const result = cleanResponse.result;
          cleanInfo.value.sc50.total_clean_area =
            result?.sc50?.total_clean_area ?? 0;
          cleanInfo.value.sc50.total_clean_time =
            result?.sc50?.total_clean_time ?? 0;
          cleanInfo.value.sc80.total_clean_area =
            result?.sc80?.total_clean_area ?? 0;
          cleanInfo.value.sc80.total_clean_time =
            result?.sc80?.total_clean_time ?? 0;
          cleanInfo.value.mix1.total_clean_area =
            result?.mix1?.total_clean_area ?? 0;
          cleanInfo.value.mix1.total_clean_time =
            result?.mix1?.total_clean_time ?? 0;
        }
      } catch (error) {
        console.error("加载清扫信息失败:", error);
      }
    };

    // 获取能耗统计信息
    const loadEnergyUsage = async () => {
      try {
        const energyResponse: EnergyUsageResponse = await Api.getEnergyUsage();
        if (energyResponse.err_no === 200) {
          const result = energyResponse.result;
          energyUsage.value.sc50.total_use_water =
            result?.sc50?.total_use_water ?? 0;
          energyUsage.value.sc50.total_power_consumption =
            result?.sc50?.total_power_consumption ?? 0;
          energyUsage.value.sc80.total_use_water =
            result?.sc80?.total_use_water ?? 0;
          energyUsage.value.sc80.total_power_consumption =
            result?.sc80?.total_power_consumption ?? 0;
          energyUsage.value.mix1.total_use_water =
            result?.mix1?.total_use_water ?? 0;
          energyUsage.value.mix1.total_power_consumption =
            result?.mix1?.total_power_consumption ?? 0;
        }
      } catch (error) {
        console.error("加载能耗统计信息失败:", error);
      }
    };

    // 获取任务结束原因统计信息
    const loadTaskEndCount = async () => {
      try {
        const endResponse: TaskEndCountResponse = await Api.getTaskEndCount();
        if (endResponse.err_no === 200) {
          const result = endResponse.result;
          const normalizeSeries = (
            source: TaskEndCountResult["sc50"] | undefined
          ) => {
            const normalized: Record<string, { task_count: number }> = {};
            taskEndReasonCodes.forEach((code) => {
              const value = source?.[code]?.task_count ?? 0;
              normalized[code] = { task_count: parseToNumber(value) };
            });
            return normalized;
          };

          taskEndCount.value.sc50 = normalizeSeries(result?.sc50);
          taskEndCount.value.sc80 = normalizeSeries(result?.sc80);
          taskEndCount.value.mix1 = normalizeSeries(result?.mix1);
        }
      } catch (error) {
        console.error("加载任务结束原因统计失败:", error);
      }
    };

    // 获取任务结束统计信息
    const loadTaskCount = async () => {
      try {
        const taskResponse: TaskCountResponse = await Api.getTaskCount();
        console.log("taskResponse", taskResponse);
        if (taskResponse.err_no === 200) {
          const result = taskResponse.result;
          taskCount.value.success_sc50.task_count =
            result?.success_sc50?.task_count ?? 0;
          taskCount.value.success_sc80.task_count =
            result?.success_sc80?.task_count ?? 0;
          taskCount.value.success_mix1.task_count =
            result?.success_mix1?.task_count ?? 0;
          taskCount.value.abnormal_sc50.task_count =
            result?.abnormal_sc50?.task_count ?? 0;
          taskCount.value.abnormal_sc80.task_count =
            result?.abnormal_sc80?.task_count ?? 0;
          taskCount.value.abnormal_mix1.task_count =
            result?.abnormal_mix1?.task_count ?? 0;
          console.log("taskCount", taskCount.value);
        }
      } catch (error) {
        console.error("加载任务结束统计失败:", error);
      }
    };
    // 初始化清洁面积统计图表
    const initAreaChart = () => {
      nextTick(() => {
        if (!areaChart.value) return;

        // 如果已存在实例，先销毁
        if (areaChartInstance) {
          areaChartInstance.dispose();
          areaChartInstance = null;
        }

        areaChartInstance = echarts.init(areaChart.value);

        // 合并日期，确保三条线的数据对齐
        const sc50Dates = cleanAreaData.value.dates_sc50 || [];
        const sc80Dates = cleanAreaData.value.dates_sc80 || [];
        const mix1Dates = cleanAreaData.value.dates_mix1 || [];
        const sc50CleanArea = cleanAreaData.value.cleanArea_sc50 || [];
        const sc80CleanArea = cleanAreaData.value.cleanArea_sc80 || [];
        const mix1CleanArea = cleanAreaData.value.cleanArea_mix1 || [];

        console.log("清洁面积数据:", {
          sc50Dates,
          sc80Dates,
          mix1Dates,
          sc50CleanArea,
          sc80CleanArea,
          mix1CleanArea,
        });

        const allDates = [
          ...new Set([...sc50Dates, ...sc80Dates, ...mix1Dates]),
        ].sort();

        // 如果没有数据，显示空图表
        if (allDates.length === 0) {
          console.warn("清洁面积数据为空");
          const emptyOption = {
            tooltip: { trigger: "axis" },
            xAxis: { type: "category", data: [] },
            yAxis: { type: "value" },
            series: [
              { name: "SC50", type: "line", data: [] },
              { name: "SC80", type: "line", data: [] },
              { name: "MIX1", type: "line", data: [] },
            ],
          };
          if (areaChartInstance) {
            areaChartInstance.setOption(emptyOption);
          }
          return;
        }

        // 对齐数据到统一的日期轴
        const sc50CleanAreaAligned = allDates.map((date) => {
          const index = sc50Dates.indexOf(date);
          return index >= 0 ? sc50CleanArea[index] || 0 : 0;
        });

        const sc80CleanAreaAligned = allDates.map((date) => {
          const index = sc80Dates.indexOf(date);
          return index >= 0 ? sc80CleanArea[index] || 0 : 0;
        });

        const mix1CleanAreaAligned = allDates.map((date) => {
          const index = mix1Dates.indexOf(date);
          return index >= 0 ? mix1CleanArea[index] || 0 : 0;
        });

        console.log("对齐后的数据:", {
          allDates,
          sc50CleanAreaAligned,
          sc80CleanAreaAligned,
          mix1CleanAreaAligned,
        });

        const option = {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
            },
          },
          legend: {
            data: ["SC50", "SC80", "MIX1"],
            top: "5%",
            left: "right",
            textStyle: {
              fontSize: 12,
            },
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            top: "15%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            data: allDates,
            axisLabel: {
              fontSize: 10,
            },
          },
          yAxis: {
            type: "value",
            splitLine: {
              lineStyle: {
                type: "dashed",
              },
            },
          },
          series: [
            {
              name: "SC50",
              type: "line",
              data: sc50CleanAreaAligned,
              smooth: true,
              lineStyle: {
                color: "#fe5636",
              },
              itemStyle: {
                color: "#fe5636",
              },
              symbol: "circle",
              symbolSize: 6,
              tooltip: {
                valueFormatter: (value: number) => {
                  if (value === null || value === undefined) {
                    return "0 m²";
                  }
                  return `${value.toLocaleString()} m²`;
                },
              },
            },
            {
              name: "SC80",
              type: "line",
              data: sc80CleanAreaAligned,
              smooth: true,
              lineStyle: {
                color: "#6f63f6",
              },
              itemStyle: {
                color: "#6f63f6",
              },
              symbol: "circle",
              symbolSize: 6,
              tooltip: {
                valueFormatter: (value: number) => {
                  if (value === null || value === undefined) {
                    return "0 m²";
                  }
                  return `${value.toLocaleString()} m²`;
                },
              },
            },
            {
              name: "MIX1",
              type: "line",
              data: mix1CleanAreaAligned,
              smooth: true,
              lineStyle: {
                color: "#8ec258",
              },
              itemStyle: {
                color: "#8ec258",
              },
              symbol: "circle",
              symbolSize: 6,
              tooltip: {
                valueFormatter: (value: number) => {
                  if (value === null || value === undefined) {
                    return "0 m²";
                  }
                  return `${value.toLocaleString()} m²`;
                },
              },
            },
          ],
        };

        if (areaChartInstance) {
          areaChartInstance.setOption(option);

          // 响应式调整
          const resizeHandler = () => {
            if (areaChartInstance) {
              areaChartInstance.resize();
            }
          };
          window.addEventListener("resize", resizeHandler);
        }
      });
    };

    // 初始化清洁时间统计图表
    const initTimeChart = () => {
      nextTick(() => {
        if (!timeChart.value) return;
        // 如果已存在实例，先销毁
        if (timeChartInstance) {
          timeChartInstance.dispose();
          timeChartInstance = null;
        }

        timeChartInstance = echarts.init(timeChart.value);

        // 合并日期，确保三条线的数据对齐
        const sc50Dates = cleanTimeData.value.dates_sc50 || [];
        const sc80Dates = cleanTimeData.value.dates_sc80 || [];
        const mix1Dates = cleanTimeData.value.dates_mix1 || [];
        const sc50Data = cleanTimeData.value.cleanTime_sc50 || [];
        const sc80Data = cleanTimeData.value.cleanTime_sc80 || [];
        const mix1Data = cleanTimeData.value.cleanTime_mix1 || [];

        console.log("清洁时长数据:", {
          sc50Dates,
          sc80Dates,
          mix1Dates,
          sc50Data,
          sc80Data,
          mix1Data,
        });

        const allDates = [
          ...new Set([...sc50Dates, ...sc80Dates, ...mix1Dates]),
        ].sort();

        // 如果没有数据，显示空图表
        if (allDates.length === 0) {
          console.warn("清洁时长数据为空");
          const emptyOption = {
            tooltip: { trigger: "axis" },
            xAxis: { type: "category", data: [] },
            yAxis: { type: "value" },
            series: [
              { name: "SC50", type: "line", data: [] },
              { name: "SC80", type: "line", data: [] },
              { name: "MIX1", type: "line", data: [] },
            ],
          };
          if (timeChartInstance) {
            timeChartInstance.setOption(emptyOption);
          }
          return;
        }

        // 对齐数据到统一的日期轴，使用 0 代替 null
        const sc50Aligned = allDates.map((date) => {
          const index = sc50Dates.indexOf(date);
          return index >= 0 ? sc50Data[index] || 0 : 0;
        });

        const sc80Aligned = allDates.map((date) => {
          const index = sc80Dates.indexOf(date);
          return index >= 0 ? sc80Data[index] || 0 : 0;
        });

        const mix1Aligned = allDates.map((date) => {
          const index = mix1Dates.indexOf(date);
          return index >= 0 ? mix1Data[index] || 0 : 0;
        });

        console.log("对齐后的数据:", {
          allDates,
          sc50Aligned,
          sc80Aligned,
          mix1Aligned,
        });

        const option = {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
            },
          },
          legend: {
            data: ["SC50", "SC80", "MIX1"],
            top: "5%",
            left: "right",
            textStyle: {
              fontSize: 12,
            },
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            top: "15%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            data: allDates,
            axisLabel: {
              fontSize: 10,
            },
            splitLine: {
              show: false,
            },
          },
          yAxis: {
            type: "value",
            splitLine: {
              lineStyle: {
                type: "dashed",
              },
            },
          },
          series: [
            {
              name: "SC50",
              type: "line",
              data: sc50Aligned,
              smooth: true,
              lineStyle: {
                color: "#fe5636",
              },
              itemStyle: {
                color: "#fe5636",
              },
              symbol: "circle",
              symbolSize: 6,
              tooltip: {
                valueFormatter: (value: number) => {
                  if (value === null || value === undefined) {
                    return "0.00 h";
                  }
                  return `${value.toFixed(2)} h`;
                },
              },
            },
            {
              name: "SC80",
              type: "line",
              data: sc80Aligned,
              smooth: true,
              lineStyle: {
                color: "#6f63f6",
              },
              itemStyle: {
                color: "#6f63f6",
              },
              symbol: "circle",
              symbolSize: 6,
              tooltip: {
                valueFormatter: (value: number) => {
                  if (value === null || value === undefined) {
                    return "0.00 h";
                  }
                  return `${value.toFixed(2)} h`;
                },
              },
            },
            {
              name: "MIX1",
              type: "line",
              data: mix1Aligned,
              smooth: true,
              lineStyle: {
                color: "#8ec258",
              },
              itemStyle: {
                color: "#8ec258",
              },
              symbol: "circle",
              symbolSize: 6,
              tooltip: {
                valueFormatter: (value: number) => {
                  if (value === null || value === undefined) {
                    return "0.00 h";
                  }
                  return `${value.toFixed(2)} h`;
                },
              },
            },
          ],
        };

        if (timeChartInstance) {
          timeChartInstance.setOption(option);

          // 响应式调整
          const resizeHandler = () => {
            if (timeChartInstance) {
              timeChartInstance.resize();
            }
          };
          window.addEventListener("resize", resizeHandler);
        }
      });
    };

    // 初始化清洁效率统计图表
    const initEfficiencyChart2 = () => {
      nextTick(() => {
        if (!efficiencyChart.value) return;

        // 如果已存在实例，先销毁
        const existingChart = echarts.getInstanceByDom(efficiencyChart.value);
        if (existingChart) {
          existingChart.dispose();
        }

        const myChart = echarts.init(efficiencyChart.value);

        // 根据筛选条件选择数据
        let maxCleanRateData: number[];
        let cleanRateDataToShow: number[];
        let datesToShow: string[];

        if (efficiencyFilter.value === "sc50") {
          maxCleanRateData = cleanRateData.value.maxCleanRate_sc50 || [];
          cleanRateDataToShow = cleanRateData.value.cleanRate_sc50 || [];
          datesToShow = cleanRateData.value.dates_sc50 || [];
        } else if (efficiencyFilter.value === "sc80") {
          maxCleanRateData = cleanRateData.value.maxCleanRate_sc80 || [];
          cleanRateDataToShow = cleanRateData.value.cleanRate_sc80 || [];
          datesToShow = cleanRateData.value.dates_sc80 || [];
        } else if (efficiencyFilter.value === "mix1") {
          maxCleanRateData = cleanRateData.value.maxCleanRate_mix1 || [];
          cleanRateDataToShow = cleanRateData.value.cleanRate_mix1 || [];
          datesToShow = cleanRateData.value.dates_mix1 || [];
        } else {
          maxCleanRateData = cleanRateData.value.maxCleanRate_all || [];
          cleanRateDataToShow = cleanRateData.value.cleanRate_all || [];
          datesToShow = cleanRateData.value.dates_all || [];
        }

        const option = {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
              crossStyle: {
                color: "#999",
              },
            },
            formatter: (params: any) => {
              const lines = params
                .map((item: any) => {
                  const value =
                    item.value === null || item.value === undefined
                      ? "0"
                      : item.value;
                  return `${item.marker}${item.seriesName}: ${value} ㎡/h`;
                })
                .join("<br/>");
              return `${params?.[0]?.axisValueLabel ?? ""}<br/>${lines}`;
            },
          },
          legend: {
            data: [t("maxCleaningEfficiency"), t("cleaningEfficiency")],
            top: "5%",
            left: "right",
            textStyle: {
              fontSize: 12,
            },
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            top: "15%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            data: datesToShow,
            axisLabel: {
              fontSize: 10,
            },
          },
          yAxis: {
            type: "value",
            splitLine: {
              lineStyle: {
                type: "dashed",
              },
            },
          },
          series: [
            {
              name: t("maxCleaningEfficiency"),
              type: "line",
              data: maxCleanRateData,
              smooth: true,
              lineStyle: {
                color: "#fe5636",
              },
              itemStyle: {
                color: "#fe5636",
              },
              symbol: "circle",
              symbolSize: 6,
            },
            {
              name: t("cleaningEfficiency"),
              type: "line",
              data: cleanRateDataToShow,
              smooth: true,
              lineStyle: {
                color: "#8ec258",
              },
              itemStyle: {
                color: "#8ec258",
              },
              symbol: "circle",
              symbolSize: 6,
            },
          ],
        };

        myChart.setOption(option);
        efficiencyChartInstance = myChart;
      });
    };

    watch(
      deviceInfo,
      () => {
        renderDeviceCharts();
      },
      { deep: true }
    );

    watch(
      cleanInfo,
      () => {
        renderCleanCharts();
      },
      { deep: true }
    );

    watch(
      taskEndCount,
      () => {
        renderTaskDataCard();
      },
      { deep: true }
    );

    watch(
      taskCount,
      () => {
        renderTaskDataCard();
      },
      { deep: true }
    );

    watch(
      energyUsage,
      () => {
        renderEnergyCharts();
      },
      { deep: true }
    );

    watch(taskStatsFilter, () => {
      renderTaskDataCard();
    });

    watch(efficiencyFilter, () => {
      initEfficiencyChart2();
    });

    onMounted(() => {
      renderDeviceCharts();
      renderCleanCharts();
      renderTaskDataCard();
      renderEnergyCharts();
      window.addEventListener("resize", handleResize);
      loadDeviceInfo();
      loadCleanInfo();
      loadCleanAreaStatistic(7); // 默认加载7天数据
      loadCleanTimeStatistic(7); // 默认加载7天数据
      loadCleanRateStatistic(7); // 默认加载7天数据
      loadEnergyUsage();
      loadTaskEndCount();
      loadTaskCount();
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleResize);
      totalDeviceChartInstance?.dispose();
      onlineDeviceChartInstance?.dispose();
      totalCleanAreaChartInstance?.dispose();
      totalCleanTimeChartInstance?.dispose();
      taskSummaryChartInstance?.dispose();
      taskReasonOverviewChartInstance?.dispose();
      timeChartInstance?.dispose();
      areaChartInstance?.dispose();
      efficiencyChartInstance?.dispose();
      energyUsageBarChartInstance?.dispose();
    });

    return {
      t,
      cleanInfo,
      energyUsage,
      taskCount,
      taskEndCount,
      deviceInfo,
      areaTimeRange,
      timeTimeRange,
      efficiencyTimeRange,
      efficiencyFilter,
      taskStatsFilter,
      changeAreaTimeRange,
      changeTimeTimeRange,
      changeEfficiencyTimeRange,
      changeEfficiencyFilter,
      changeTaskStatsFilter,
      loadDeviceInfo,
      loadCleanInfo,
      loadCleanAreaStatistic,
      loadCleanTimeStatistic,
      loadCleanRateStatistic,
      totalDeviceCount,
      totalOnlineCount,
      totalDeviceChart,
      onlineDeviceChart,
      totalCleanAreaChart,
      totalCleanTimeChart,
      timeChart,
      areaChart,
      efficiencyChart,
      energyUsageBarChart,
      totalCleanArea,
      totalCleanTime,
      totalWaterUsage,
      totalPowerConsumption,
      taskSummaryChart,
      taskReasonOverviewChart,
      formatNumber,
      cleanAreaData,
      cleanTimeData,
      cleanRateData,
      loadEnergyUsage,
      loadTaskEndCount,
      loadTaskCount,
      deviceDistribution,
      onlineDistribution,
      taskSummaryDisplay,
    };
  },
});
